import React from 'react';
import styled from '@benzinga/themetron';
import classNames from 'classnames';
import Image from 'next/image';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import { SectionTitle } from '@benzinga/core-ui';
import { Carousel, CarouselArrowPosition } from '@benzinga/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons';

interface CarouselAnalytics {
  carouselName: string;
  category: string;
}

export interface CarouselBlockProps {
  attrs: {
    data: CarouselData;
  };
  items: ItemProps[];
  analytics?: CarouselAnalytics;
}

export interface CarouselData {
  title?: string;
  link?: string;
  variant?: 'default' | 'reverse' | 'on-block-arrows';
  layout?: 'default' | 'india';
  options?: Options;
}

interface Options {
  height?: number;
  width?: number;
  object_fit?: 'contain' | 'cover';
  show_arrows_only_on_overflow?: boolean;
  title_case?: boolean;
  slides_per_page?: number;
  background_color?: string;
}

export interface ItemProps {
  href?: string;
  id: string | number;
  title: string;
  subtitle?: string;
  image: string;
  options?: Options;
  analytics?: CarouselAnalytics;
  sectionTitle?: string;
  isBzProPost?: boolean;
  isBzPost?: boolean;
  variant?: string;
}

const BZ_PLACEHOLDER = '/next-assets/images/benzinga-post-card-gray-placeholder.svg';
const BZ_PRO_PLACEHOLDER = '/next-assets/images/benzinga-pro-post-card-placeholder-light.svg';

const indialayoutProps = {
  arrowPosition: 'on-block-arrows' as CarouselArrowPosition,
  borderBottom: false,
  carouselItemVariant: 'secondary',
  roundedBorder: true,
  useFontAwesomeIcon: true,
};

export const CarouselBlock: React.FC<CarouselBlockProps> = ({ analytics, attrs, items: itemsProps }) => {
  const items = itemsProps ?? [];
  const title = attrs?.data?.title;
  const titleUrl = attrs?.data?.link;
  const variant = attrs?.data?.variant ?? 'default';
  const titleCase = attrs?.data?.options?.title_case ?? false;
  const layout = attrs?.data?.layout;

  const layoutProps =
    layout === 'india'
      ? indialayoutProps
      : {
          arrowPosition: 'right' as CarouselArrowPosition,
          borderBottom: true,
          carouselItemVariant: 'default',
          roundedBorder: false,
          useFontAwesomeIcon: false,
        };

  const showArrowsOnlyOnOverflow = true;

  const slidesPerPage =
    attrs?.data?.options?.slides_per_page ?? (Number(items?.length) > 3 ? 4 : Number(items?.length));

  return (
    <Container
      borderBottom={layoutProps.borderBottom}
      className={classNames('carousel-block', { [`${variant}`]: variant })}
    >
      {title && (
        <SectionTitle
          bordered={true}
          rightSlot={
            titleUrl && (
              <span className="see-more">
                {layoutProps.useFontAwesomeIcon ? (
                  <FontAwesomeIcon
                    color="rgb(36, 52, 79)"
                    fontSize={20}
                    fontWeight={600}
                    icon={faArrowUpRightFromSquare}
                  />
                ) : (
                  'See more'
                )}
              </span>
            )
          }
          titleText={title ? `${title} Carousel` : 'Carousel'}
          uppercase={!titleCase}
          url={titleUrl}
        >
          {title}
        </SectionTitle>
      )}
      {Array.isArray(items) && (
        <Carousel
          analytics={analytics}
          arrowsPosition={layoutProps.arrowPosition}
          component={(item: ItemProps) => (
            <CarouselItem
              {...item}
              options={attrs?.data?.options}
              sectionTitle={title}
              variant={layoutProps.carouselItemVariant}
            />
          )}
          items={items}
          options={{ focus: 1, perPage: slidesPerPage }}
          roundedBorder={layoutProps.roundedBorder}
          showArrowsOnlyOnOverflow={showArrowsOnlyOnOverflow}
        />
      )}
    </Container>
  );
};

export const Container = styled.div<{ borderBottom: boolean }>`
  &.carousel-block {
    margin: 1rem 0;
    overflow: hidden;

    &.reverse {
      .title {
        margin-top: 10px;
        -webkit-line-clamp: 3;
      }
      .carousel-item-wrapper {
        display: flex;
        flex-direction: column-reverse;
      }
    }

    .section-title {
      margin-bottom: 15px;
      border-bottom: ${({ borderBottom, theme }) =>
        borderBottom ? `solid 1px ${theme.colorPalette.neutral500}` : 'none'};
    }

    .carousel-container .carousel-pagination {
      text-align: center;
      margin-top: 12px;
      margin-bottom: 24px;
      display: block;

      > div {
        align-items: center;
        justify-content: center;

        button {
          width: 90px;
          height: 32px;
        }
      }
    }
  }
`;

const CarouselItem: React.FC<ItemProps> = ({
  analytics,
  href,
  id,
  image,
  isBzPost,
  isBzProPost,
  options,
  sectionTitle,
  subtitle,
  title,
  variant = 'default',
}) => {
  const height = options?.height ? Number(options?.height) : 200;
  const width = options?.width ? Number(options?.width) : 300;
  const objectFit = options?.object_fit ? options.object_fit : 'cover';
  const cardDataAction = sectionTitle ? `${sectionTitle} Carousel Card Item Click` : 'Carousel Item Click';
  const backgroundColor = options?.background_color;
  const isProOnlyPost = !isBzPost && isBzProPost;

  return (
    <CardWrapper
      className="carousel-item-wrapper item"
      data-action={cardDataAction}
      data-category={analytics?.category}
      href={href}
      key={id}
      target="_blank"
      width={width}
    >
      {variant === 'default' && (
        <CardContent>
          <Title className="title" dangerouslySetInnerHTML={{ __html: sanitizeHTML(title) }} />
          {subtitle && <Subtitle dangerouslySetInnerHTML={{ __html: sanitizeHTML(subtitle) }} />}
        </CardContent>
      )}
      <Thumbnail
        $backgroundColor={backgroundColor}
        $height={height}
        $variant={variant}
        $width={width}
        className="carousel-item-thumbnail"
      >
        <Image
          alt=""
          fill
          sizes={`${width}px`}
          src={image || (isProOnlyPost ? BZ_PRO_PLACEHOLDER : BZ_PLACEHOLDER)}
          style={{ objectFit }}
        />
        {variant === 'secondary' && (
          <>
            <SecondaryTitle dangerouslySetInnerHTML={{ __html: sanitizeHTML(title) }} />
            <RadialGradient />
          </>
        )}
      </Thumbnail>
    </CardWrapper>
  );
};

const Title = styled.div`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.foreground};
  text-overflow: ellipsis;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
`;

const SecondaryTitle = styled.div`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: white;
  position: absolute;
  top: 100px;
  left: 5px;
  z-index: 2;
`;

const Subtitle = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.foregroundMuted};
  text-overflow: ellipsis;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
`;

const CardContent = styled.div`
  margin-bottom: 8px;
`;

const CardWrapper = styled.a<{ width?: number }>`
  display: block;
  width: ${({ width }) => width}px;
  margin-right: 15px;

  &:last-of-type {
    margin-right: 0;
  }
`;

const Thumbnail = styled.div<{ $backgroundColor?: string; $variant?: string; $width?: number; $height?: number }>`
  position: relative;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  height: ${({ $height }) => $height}px;
  width: ${({ $width }) => $width}px;
  overflow: hidden;
  background-color: ${({ $backgroundColor, theme }) => $backgroundColor || theme.colors.background};
  border-radius: ${({ $variant }) => ($variant === 'secondary' ? '8px' : '0px')};
`;

const RadialGradient = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(83.22% 75.41% at 64.51% 26%, rgba(255, 255, 255, 0) 32%, #192940 99%);
  border-radius: 8px;
`;

export default CarouselBlock;
